import { ContentLoading } from '@/components/common/content-loading';
import { ChangeRegionButton, NavigationGuide, NearbyPoiListDropdown, ZoomControlBtnGroup } from '@/components/feature/floor-guide';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon } from '@/components/icons';
import { ParkingFloorHeader } from '@/components/feature/parking/horizontal/floor-header';
import { ParkingSlotList } from '@/components/feature/parking/horizontal/parking-slot-list';
import { NavigationStatus } from '@/enums/floor-guide';
import { useParkingMapView } from '@/hooks/parking';
import { generateFloorName } from '@/utils/floor-guide';
import { generateQrHereUrl } from '@/utils/map';
import { getPoiTitleByLanguage } from '@/utils/map-poi';
import { generateDisplayDirections } from '@/utils/route-simulation';
import clsx from 'clsx';
import styles from './styles.module.scss';

interface HorizontalViewProps {
    readonly gnbMenuId?: string;
}

export default function HorizontalView({ gnbMenuId }: HorizontalViewProps) {
    const {
        t,
        mapContainerRef,
        containerRef,
        parkingSlotListRef,
        server,
        zoomClosed,
        language,
        mapData,
        loading,
        isMapReady,
        isDisabledZoomIn,
        isDisabledZoomOut,
        handleZoomIn,
        handleZoomOut,
        moveUp,
        moveDown,
        moveLeft,
        moveRight,
        showPositionPopup,
        showNavigationGuide,
        navigationStatus,
        selectedPoiRef,
        selectedRoute,
        startPoint,
        navigationGuideEndPoint,
        nearbyPoiList,
        nearbySelectedPoiId,
        nearbyTitle,
        nearbyFloorName,
        menuName,
        parkingSlots,
        selectedSlotId,
        handleSelectSlot,
        handleEndAndReset,
        handleShowMyLocation,
        handleDirections,
        handlePauseSimulation,
        handleResumeSimulation,
        handleWatchAgain,
    } = useParkingMapView(gnbMenuId);

    return (
        <section className={styles['parking-horizontal-view']} ref={containerRef}>
            <section className={styles['parking-horizontal-view__main']}>
                <section className={styles['parking-horizontal-view__sidebar']}>
                    <ParkingFloorHeader title={menuName} tabIndex={1} ariaLabel={menuName} />
                    {isMapReady && (
                        <ParkingSlotList
                            ref={parkingSlotListRef}
                            variant="sidebar"
                            slots={parkingSlots}
                            selectedSlotId={selectedSlotId}
                            onSelectSlot={handleSelectSlot}
                            onShowMyLocation={handleShowMyLocation}
                        />
                    )}
                </section>
                <section className={styles['parking-horizontal-view__content']}>
                    <div className={styles['parking-horizontal-view__map-container']}>
                        <div className="h-full w-full" style={{ pointerEvents: zoomClosed ? 'auto' : 'none' }}>
                            <div className="h-full w-full" ref={mapContainerRef} />
                        </div>
                        <section className={styles['parking-horizontal-view__zoom-control']}>
                            <ZoomControlBtnGroup
                                tabIndex={4}
                                regionOrder={4}
                                disabledZoomIn={isDisabledZoomIn}
                                disabledZoomOut={isDisabledZoomOut}
                                onZoomIn={handleZoomIn}
                                onZoomOut={handleZoomOut}
                            />
                        </section>
                        <div data-region-nav={!zoomClosed} data-region-order={5}>
                            <div
                                tabIndex={-1}
                                className={clsx(styles['parking-horizontal-view__change-zoom'], styles['parking-horizontal-view__change-zoom--up'], {
                                    hidden: zoomClosed,
                                    block: !zoomClosed,
                                })}
                            >
                                <ChangeRegionButton
                                    tabIndex={4}
                                    data-interactive
                                    aria-label={t('floor_guide.text_to_speech.move.up')}
                                    icon={<ChevronUpIcon />}
                                    onClick={moveUp}
                                    data-role={t('common.data_role.button')}
                                />
                            </div>
                            <div
                                tabIndex={-1}
                                className={clsx(styles['parking-horizontal-view__change-zoom'], styles['parking-horizontal-view__change-zoom--left'], {
                                    hidden: zoomClosed,
                                    block: !zoomClosed,
                                })}
                            >
                                <ChangeRegionButton
                                    tabIndex={4}
                                    data-interactive
                                    aria-label={t('floor_guide.text_to_speech.move.left')}
                                    icon={<ChevronLeftIcon />}
                                    onClick={moveLeft}
                                    data-role={t('common.data_role.button')}
                                />
                            </div>
                            <div
                                tabIndex={-1}
                                className={clsx(styles['parking-horizontal-view__change-zoom'], styles['parking-horizontal-view__change-zoom--down'], {
                                    hidden: zoomClosed,
                                    block: !zoomClosed,
                                })}
                            >
                                <ChangeRegionButton
                                    tabIndex={5}
                                    data-interactive
                                    aria-label={t('floor_guide.text_to_speech.move.down')}
                                    icon={<ChevronDownIcon />}
                                    onClick={moveDown}
                                    data-role={t('common.data_role.button')}
                                />
                            </div>
                            <div
                                tabIndex={-1}
                                className={clsx(styles['parking-horizontal-view__change-zoom'], styles['parking-horizontal-view__change-zoom--right'], {
                                    hidden: zoomClosed,
                                    block: !zoomClosed,
                                })}
                            >
                                <ChangeRegionButton
                                    tabIndex={4}
                                    data-interactive
                                    aria-label={t('floor_guide.text_to_speech.move.right')}
                                    icon={<ChevronRightIcon />}
                                    onClick={moveRight}
                                    data-role={t('common.data_role.button')}
                                />
                            </div>
                        </div>
                        {showPositionPopup && (
                            <div className={styles['parking-horizontal-view__position-popup']}>
                                <NearbyPoiListDropdown
                                    type="show-location"
                                    isActive={showPositionPopup}
                                    poiList={nearbyPoiList}
                                    selectedPoiId={nearbySelectedPoiId}
                                    title={nearbyTitle}
                                    floorName={nearbyFloorName}
                                    buildingName=""
                                    onSelectPoi={() => {}}
                                    onStartNavigationGuide={() => handleDirections()}
                                    onClose={handleEndAndReset}
                                />
                            </div>
                        )}
                        {showNavigationGuide && (
                            <div className={styles['parking-horizontal-view__navigation-guide']}>
                                <NavigationGuide
                                    isActive={showNavigationGuide}
                                    isShowPauseBtn={navigationStatus === NavigationStatus.RUNNING}
                                    isShowResumeBtn={navigationStatus === NavigationStatus.PAUSED}
                                    isShowWatchAgainBtn={navigationStatus === NavigationStatus.DONE}
                                    directionMaxVisibleRows={3}
                                    qrHereUrl={generateQrHereUrl(server?.store?.qrHereId ?? '', startPoint, selectedPoiRef?.id ?? '')}
                                    buildingName=""
                                    directions={generateDisplayDirections(
                                        t,
                                        (floorId: string) => {
                                            const floor = mapData?.dataFloor?.getFloors()?.find((f: any) => f.id === floorId);
                                            const floorName = floor?.name?.[0]?.text ?? '';
                                            return t('floor_guide.text_to_speech.floor', { floorName: generateFloorName(floorName) });
                                        },
                                        selectedRoute?.navigationList,
                                        getPoiTitleByLanguage(selectedPoiRef, language)
                                    )}
                                    startFloorName={server?.floor?.name?.[language] || server?.floor?.name?.['ko'] || ''}
                                    endPoint={navigationGuideEndPoint}
                                    onStop={handleEndAndReset}
                                    onResume={handleResumeSimulation}
                                    onPause={handlePauseSimulation}
                                    onWatchAgain={handleWatchAgain}
                                />
                            </div>
                        )}
                    </div>
                </section>
            </section>
            {loading && <ContentLoading loading={loading} />}
        </section>
    );
}
